import i18n from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSharingApi } from "@/hooks/useSharingApi";
import type { PendingInvite } from "@/lib/api";
import { inviteLink } from "@/lib/sharing/pendingInvites";

/** How often an open drawer looks for someone joining. */
export const JOIN_POLL_MS = 10_000;
/** How long a row that just joined stays highlighted. */
export const JOIN_FLASH_MS = 2_500;

export interface PendingInvites {
  invites: PendingInvite[];
  /** A line under a row after Resend or Copy, keyed by invite id. */
  notes: Readonly<Record<string, string>>;
  refresh: () => Promise<void>;
  resend: (invite: PendingInvite) => Promise<void>;
  copyLink: (invite: PendingInvite) => Promise<void>;
  cancel: (invite: PendingInvite) => Promise<void>;
}

/**
 * A document's invitations not yet accepted (Olivia, 10 Oct, §c), for the
 * desktop drawer and the phone's Share sheet. While open it looks again every
 * ten seconds and when the window regains focus, and asks the people list to
 * look again too (`onLook`): someone who joins moves from one to the other,
 * however quickly they joined. `useNewArrivals` lights their row up.
 */
export function usePendingInvites(documentId: string, enabled: boolean, onLook: () => void, onError?: (message: string) => void): PendingInvites {
  const api = useSharingApi();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const onLookRef = useRef(onLook);
  onLookRef.current = onLook;

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      setInvites(await api.listInvites(documentId));
    } catch {
      // Keep what's shown; the next look tries again.
    }
  }, [api, documentId, enabled]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const look = () => {
      void refresh();
      onLookRef.current();
    };
    const timer = window.setInterval(look, JOIN_POLL_MS);
    const onFocus = look;
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, refresh]);

  const note = useCallback((id: string, text: string) => {
    setNotes((n) => ({ ...n, [id]: text }));
    window.setTimeout(() => setNotes((n) => {
      const rest = { ...n };
      delete rest[id];
      return rest;
    }), 4000);
  }, []);

  const resend = useCallback(async (invite: PendingInvite) => {
    try {
      const r = await api.resendInvite(documentId, invite.id);
      if (!r.success) {
        onError?.(r.message || i18n.t("sharing:pending.notSent"));
        return;
      }
      note(invite.id, i18n.t(r.userFound ? "sharing:pending.hasAccount" : "sharing:pending.sentAgain"));
      await refresh();
    } catch {
      onError?.(i18n.t("sharing:pending.notSentConnection"));
    }
  }, [api, documentId, note, onError, refresh]);

  const copyLink = useCallback(async (invite: PendingInvite) => {
    try {
      await navigator.clipboard.writeText(inviteLink(invite.id));
      note(invite.id, i18n.t("sharing:pending.linkCopied", { email: invite.email }));
    } catch {
      onError?.(i18n.t("sharing:pending.copyFailed"));
    }
  }, [note, onError]);

  const cancel = useCallback(async (invite: PendingInvite) => {
    setInvites((list) => list.filter((i) => i.id !== invite.id));
    try {
      await api.cancelInvite(documentId, invite.id);
    } catch {
      onError?.(i18n.t("sharing:pending.saveFailed"));
    }
    await refresh();
  }, [api, documentId, onError, refresh]);

  return { invites, notes, refresh, resend, copyLink, cancel };
}

/**
 * Ids that appeared since the list first loaded, for {@link JOIN_FLASH_MS}:
 * the row of someone who just joined (or was just added) lights up.
 */
export function useNewArrivals(ids: readonly string[], loaded: boolean): ReadonlySet<string> {
  const seen = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const key = useMemo(() => [...ids].sort().join("|"), [ids]);
  useEffect(() => {
    if (!loaded) return;
    const now = key ? key.split("|") : [];
    if (!seen.current) {
      seen.current = new Set(now);
      return;
    }
    const added = now.filter((id) => !seen.current!.has(id));
    seen.current = new Set(now);
    if (added.length === 0) return;
    setFresh(new Set(added));
    const t = window.setTimeout(() => setFresh(new Set()), JOIN_FLASH_MS);
    return () => window.clearTimeout(t);
  }, [key, loaded]);
  return fresh;
}
